import React from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    DateField,
    required,
    minValue,
    maxValue,
    maxLength,
    useTranslate,
    useRedirect,
    useNotify,
    Toolbar,
    SaveButton,
    DeleteButton,
    regex,
    useRecordContext,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Alert,
    Chip,
} from '@mui/material';
import {
    ArrowBack,
    Palette,
    Category,
    Speed,
    Info,
} from '@mui/icons-material';
import { SimpleColorPicker } from '../common/SimpleColorPicker';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const CHARGER_TYPE_KNOWN_FIELDS = new Set([
    'id', 'name', 'displayName', 'minPowerKw', 'maxPowerKw',
    'colorCode', 'icon', 'displayOrder',
]);

const mapChargerTypeBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const chargerTypeFieldLabelKey = (field: string): string =>
    CHARGER_TYPE_KNOWN_FIELDS.has(field)
        ? `resources.charger-types.fields.${field}`
        : field;

const EditToolbar = () => {
    const translate = useTranslate();

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                display: 'flex',
                justifyContent: 'space-between',
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
            <DeleteButton
                confirmTitle={translate('resources.charger-types.delete.confirmTitle')}
                confirmContent={translate('resources.charger-types.delete.confirmContent')}
                sx={{
                    color: '#F44336',
                    '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const record = useRecordContext();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/charger-types')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.charger-types.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.charger-types.edit.title')}
                    </Typography>
                    {record && (
                        <Chip
                            label={record.displayName}
                            size="small"
                            sx={{
                                backgroundColor: record.colorCode ? `${record.colorCode}20` : '#E0E0E0',
                                color: record.colorCode || '#000',
                                fontWeight: 600,
                                border: `1px solid ${record.colorCode || '#E0E0E0'}`,
                            }}
                        />
                    )}
                </Box>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.charger-types.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const ChargerTypeEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    const transform = (data: any) => {
        const cleanedData = { ...data };

        delete cleanedData.id;

        if (cleanedData.minPowerKw !== undefined) {
            cleanedData.minPowerKw = Number(cleanedData.minPowerKw);
        }
        if (cleanedData.maxPowerKw !== undefined) {
            cleanedData.maxPowerKw = Number(cleanedData.maxPowerKw);
        }
        if (cleanedData.displayOrder !== undefined) {
            cleanedData.displayOrder = Number(cleanedData.displayOrder);
        }

        return cleanedData;
    };

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            redirect="list"
            transform={transform}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.charger-types.notifications.updated', { type: 'success' });
                    redirect('/charger-types');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapChargerTypeBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(chargerTypeFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Category sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-types.sections.basic')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="id"
                                            disabled
                                            fullWidth
                                            helperText={translate('resources.charger-types.helpers.idFixed')}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    backgroundColor: '#F5F5F5',
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="name"
                                            validate={[
                                                required(),
                                                maxLength(100, translate("resources.charger-types.validation.nameTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.charger-types.helpers.name')}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <TextInput
                                        source="displayName"
                                        validate={[
                                            required(),
                                            maxLength(100, translate("resources.charger-types.validation.displayNameTooLong")),
                                        ]}
                                        fullWidth
                                        helperText={translate('resources.charger-types.helpers.displayName')}
                                    />
                                </Box>

                                <Alert severity="warning" icon={<Info />}>
                                    {translate('resources.charger-types.info.idCannotChange')}
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Speed sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-types.sections.power')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="minPowerKw"
                                            validate={[
                                                minValue(0, translate("resources.charger-types.validation.powerNegative")),
                                                maxValue(1000, translate("resources.charger-types.validation.powerTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.charger-types.helpers.minPower')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="maxPowerKw"
                                            validate={[
                                                minValue(0, translate("resources.charger-types.validation.powerNegative")),
                                                maxValue(1000, translate("resources.charger-types.validation.powerTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.charger-types.helpers.maxPower')}
                                        />
                                    </Box>
                                </Box>

                                <Alert severity="info">
                                    {translate('resources.charger-types.info.powerNote')}
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Palette sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-types.sections.display')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SimpleColorPicker
                                            source="colorCode"
                                            label={translate('resources.charger-types.fields.colorCode')}
                                            validate={[
                                                regex(/^#[0-9A-Fa-f]{6}$/, translate("resources.charger-types.validation.colorFormat")),
                                            ]}
                                            helperText={translate('resources.charger-types.helpers.colorCode')}
                                            fullWidth
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="icon"
                                            validate={[
                                                maxLength(100, translate("resources.charger-types.validation.iconTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.charger-types.helpers.icon')}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <WheelSafeNumberInput
                                        source="displayOrder"
                                        validate={[
                                            minValue(0, translate("resources.charger-types.validation.orderNegative")),
                                            maxValue(100, translate("resources.charger-types.validation.orderTooHigh")),
                                        ]}
                                        fullWidth
                                        helperText={translate('resources.charger-types.helpers.displayOrder')}
                                    />
                                </Box>

                                <Alert severity="info">
                                    {translate('resources.charger-types.info.displayNote')}
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#9E9E9E' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.charger-types.sections.metadata')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                        {translate('resources.charger-types.fields.createdAt')}
                                    </Typography>
                                    <DateField source="createdAt" showTime />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                        {translate('resources.charger-types.fields.updatedAt')}
                                    </Typography>
                                    <DateField source="updatedAt" showTime />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};
